import * as satellite from "satellite.js";

let activeSatrec = null;
const AU_KM = 149_597_870.7;
const EARTH_RADIUS_KM = 6378.137;
const SUN_RADIUS_KM = 696_340;

export function satrecFromOrbit(orbit) {
  if (orbit?.format === "omm" && orbit.elements) {
    return satellite.json2satrec(orbit.elements);
  }
  if (orbit?.line1 && orbit?.line2) {
    return satellite.twoline2satrec(orbit.line1, orbit.line2);
  }
  throw new Error("Ratatiedon muotoa ei tunnistettu.");
}

export function setActiveOrbit(orbit) {
  activeSatrec = satrecFromOrbit(orbit);
}

export function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

export function shortestAngle(target, current) {
  return ((target - current + 540) % 360) - 180;
}

export function isPassActive(pass, date, elevation) {
  if (!pass?.rise || !pass?.set || !Number.isFinite(elevation)) return false;
  const stamp = date.getTime();
  return elevation >= 0 && stamp >= pass.rise.getTime() && stamp <= pass.set.getTime();
}

export function isPassImminent(pass, date, windowMs = 30 * 60_000) {
  if (!pass?.rise) return false;
  const wait = pass.rise.getTime() - date.getTime();
  return wait >= 0 && wait <= windowMs;
}

export function lookAnglesForSatrec(satrec, date, latitude, longitude, altitudeMeters = 0) {
  const propagated = satellite.propagate(satrec, date);
  if (!propagated.position) throw new Error("Rataa ei voitu laskea tälle ajalle.");

  const gmst = satellite.gstime(date);
  const observer = {
    latitude: satellite.degreesToRadians(latitude),
    longitude: satellite.degreesToRadians(longitude),
    height: altitudeMeters / 1000,
  };
  const ecf = satellite.eciToEcf(propagated.position, gmst);
  const look = satellite.ecfToLookAngles(observer, ecf);

  return {
    azimuth: normalizeDegrees(satellite.radiansToDegrees(look.azimuth)),
    elevation: satellite.radiansToDegrees(look.elevation),
    rangeKm: look.rangeSat,
  };
}

export function sunElevation(date, latitude, longitude, altitudeMeters = 0) {
  const sun = satellite.sunPos(satellite.jday(date)).rsun;
  const gmst = satellite.gstime(date);
  const observer = {
    latitude: satellite.degreesToRadians(latitude),
    longitude: satellite.degreesToRadians(longitude),
    height: altitudeMeters / 1000,
  };
  const ecf = satellite.eciToEcf({ x: sun[0] * AU_KM, y: sun[1] * AU_KM, z: sun[2] * AU_KM }, gmst);
  return satellite.radiansToDegrees(satellite.ecfToLookAngles(observer, ecf).elevation);
}

export function isEciPositionSunlit(position, sunVectorAu) {
  const sunLength = Math.hypot(...sunVectorAu);
  if (!sunLength) return false;
  const sunDirection = sunVectorAu.map((value) => value / sunLength);
  const axial = position.x * sunDirection[0] + position.y * sunDirection[1] + position.z * sunDirection[2];
  if (axial >= 0) return true;
  const radiusSquared = position.x ** 2 + position.y ** 2 + position.z ** 2;
  const crossAxisDistance = Math.sqrt(Math.max(0, radiusSquared - axial ** 2));
  const umbraRadius = EARTH_RADIUS_KM - (-axial) * (SUN_RADIUS_KM - EARTH_RADIUS_KM) / (sunLength * AU_KM);
  return umbraRadius <= 0 || crossAxisDistance > umbraRadius;
}

export function isSatelliteSunlit(satrec, date) {
  const propagated = satellite.propagate(satrec, date);
  if (!propagated.position) return false;
  return isEciPositionSunlit(propagated.position, satellite.sunPos(satellite.jday(date)).rsun);
}

export function passVisibility(satrec, pass, latitude, longitude, altitudeMeters = 0, twilightDegrees = -6) {
  const duration = pass.set.getTime() - pass.rise.getTime();
  let hasDarkSky = false;
  let visibleSamples = 0;
  let maxVisibleElevation = -90;
  let minVisibleRangeKm = Infinity;
  let bestTime = null;
  const sampleCount = Math.max(12, Math.ceil(duration / 15_000));
  for (let index = 0; index <= sampleCount; index += 1) {
    const time = new Date(pass.rise.getTime() + duration * index / sampleCount);
    const look = lookAnglesForSatrec(satrec, time, latitude, longitude, altitudeMeters);
    if (look.elevation < 0 || sunElevation(time, latitude, longitude, altitudeMeters) > twilightDegrees) continue;
    hasDarkSky = true;
    if (!isSatelliteSunlit(satrec, time)) continue;
    visibleSamples += 1;
    if (look.elevation > maxVisibleElevation) {
      maxVisibleElevation = look.elevation;
      bestTime = time;
    }
    minVisibleRangeKm = Math.min(minVisibleRangeKm, look.rangeKm);
  }
  const sampleDurationMs = duration / sampleCount;
  return {
    observable: visibleSamples > 0,
    reason: visibleSamples > 0 ? "observable" : hasDarkSky ? "earth-shadow" : "daylight",
    time: bestTime,
    visibleDurationMs: Math.min(duration, visibleSamples * sampleDurationMs),
    maxVisibleElevation: visibleSamples > 0 ? maxVisibleElevation : null,
    minVisibleRangeKm: Number.isFinite(minVisibleRangeKm) ? minVisibleRangeKm : null,
  };
}

export function lookAngles(date, latitude, longitude, altitudeMeters = 0) {
  if (!activeSatrec) throw new Error("Seurattavaa rataobjektia ei ole valittu.");
  return lookAnglesForSatrec(activeSatrec, date, latitude, longitude, altitudeMeters);
}

function refineCrossing(satrec, low, high, latitude, longitude, rising) {
  let left = low.getTime();
  let right = high.getTime();
  for (let index = 0; index < 18; index += 1) {
    const middle = (left + right) / 2;
    const above = lookAnglesForSatrec(satrec, new Date(middle), latitude, longitude).elevation >= 0;
    if (above === rising) right = middle;
    else left = middle;
  }
  return new Date((left + right) / 2);
}

function refinePeak(satrec, center, latitude, longitude, radiusMs) {
  let left = center.getTime() - radiusMs;
  let right = center.getTime() + radiusMs;
  for (let index = 0; index < 24; index += 1) {
    const third = (right - left) / 3;
    const first = left + third;
    const second = right - third;
    const firstElevation = lookAnglesForSatrec(satrec, new Date(first), latitude, longitude).elevation;
    const secondElevation = lookAnglesForSatrec(satrec, new Date(second), latitude, longitude).elevation;
    if (firstElevation < secondElevation) left = first;
    else right = second;
  }
  const time = new Date((left + right) / 2);
  return { time, elevation: lookAnglesForSatrec(satrec, time, latitude, longitude).elevation };
}

export function nextPassForSatrec(satrec, start, latitude, longitude, hours = 30, stepMs = 20_000) {
  const end = start.getTime() + hours * 3_600_000;
  let previousTime = new Date(start);
  let previousElevation = lookAnglesForSatrec(satrec, previousTime, latitude, longitude).elevation;
  let rise = previousElevation >= 0 ? new Date(start) : null;
  let peak = rise ? { time: new Date(start), elevation: previousElevation } : null;

  for (let stamp = start.getTime() + stepMs; stamp <= end; stamp += stepMs) {
    const time = new Date(stamp);
    const elevation = lookAnglesForSatrec(satrec, time, latitude, longitude).elevation;

    if (!rise && previousElevation < 0 && elevation >= 0) {
      rise = refineCrossing(satrec, previousTime, time, latitude, longitude, true);
      peak = { time, elevation };
    }
    if (rise && elevation > peak.elevation) peak = { time, elevation };
    if (rise && previousElevation >= 0 && elevation < 0) {
      const set = refineCrossing(satrec, previousTime, time, latitude, longitude, false);
      peak = refinePeak(satrec, peak.time, latitude, longitude, stepMs);
      return { rise, peak, set };
    }

    previousTime = time;
    previousElevation = elevation;
  }
  return null;
}

export function nextPass(start, latitude, longitude, hours = 30) {
  if (!activeSatrec) throw new Error("Seurattavaa rataobjektia ei ole valittu.");
  return nextPassForSatrec(activeSatrec, start, latitude, longitude, hours);
}

export function passesForSatrec(satrec, start, latitude, longitude, hours = 48, maxPasses = 30) {
  const end = start.getTime() + hours * 3_600_000;
  const passes = [];
  let cursor = new Date(start);
  while (cursor.getTime() < end && passes.length < maxPasses) {
    const remainingHours = (end - cursor.getTime()) / 3_600_000;
    const pass = nextPassForSatrec(satrec, cursor, latitude, longitude, remainingHours, 60_000);
    if (!pass) break;
    passes.push(pass);
    cursor = new Date(pass.set.getTime() + 60_000);
  }
  return passes;
}
